import { getCookieValue, COOKIES } from '@/utils/cookies';
import { getConfig } from '@/utils/config/config';
import { buildApiUrl } from '@/utils/url/build-url';
import { fetchApi } from './fetcher';
import type { CustomerPreferences } from '@/types/user-context';

const getHeaders = async () => {
    const authToken = await getCookieValue(COOKIES.AUTH_TOKEN);
    const loginToken = await getCookieValue(COOKIES.LOGIN_TOKEN);

    return {
        'Content-Type': 'application/json',
        'X-Auth-Token': authToken,
        'X-Login-Token': loginToken,
        'Cache-Control': 'no-store, no-cache'
    };
};

export const updateCustomerPreferences = async (preferences: CustomerPreferences) => {
    if (typeof window === 'undefined') {
        return;
    }

    const config = getConfig();
    const host = buildApiUrl(config.apis.CustomerDataClient);
    const headers = await getHeaders();
    const networkConfig = config.network?.InternalCustomerDataClient;

    const response = await fetchApi(
        `${host}/preferences`,
        {
            method: 'PATCH',
            headers,
            credentials: 'include',
            body: JSON.stringify(preferences)
        },
        networkConfig ? { networkConfig } : undefined
    );

    if (!response.ok) {
        throw new Error(`Failed to update customer preferences: ${response.status}`);
    }

    return response;
};

export const getUserPreferences = async (): Promise<CustomerPreferences | null> => {
    if (typeof window === 'undefined') {
        return null;
    }

    const config = getConfig();
    const host = buildApiUrl(config.apis.CustomerDataClient);
    const headers = await getHeaders();

    const url = `${host}/preferencesV2`;
    const networkConfig = config.network?.InternalCustomerDataClient;

    try {
        const response = await fetchApi(
            url,
            {
                method: 'GET',
                headers,
                credentials: 'include'
            },
            networkConfig ? { networkConfig } : undefined
        );

        if (!response.ok) {
            throw new Error(`Failed to get customer preferences: ${response.status}`);
        }

        const data = await response.json();
        return data as CustomerPreferences;
    } catch (error) {
        console.error(`Error fetching customer preferences: ${(error as Error).message}`);
        return null;
    }
};
